import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Send, 
  Bot, 
  User, 
  Loader2, 
  Sparkles,
  MessageCircle,
  Lightbulb,
  BookOpen,
  HelpCircle,
  Shield
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import DashboardLayout from "../components/DashboardLayout";
import { EarnMoneyBanner } from "../components/Client/PlanSection";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HelpCenter = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `¡Bienvenido al Centro de Asesoría de Divídelo! 👋

Soy tu Asistente Migratorio Virtual, especializado en visas B1/B2 de Estados Unidos. Estoy aquí para guiarte en cada paso de tu proceso.

Puedo ayudarte con:
• Requisitos y documentación
• Preparación para la entrevista
• El formulario DS-160
• Estrategias para fortalecer tu perfil

¿En qué puedo ayudarte hoy?

💡 CONSEJO DIVÍDELO: Recuerda que mientras más preparado estés, mayor confianza tendrás en tu entrevista consular.`
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/chat/history`);
      if (res.data && res.data.length > 0) {
        const history = res.data.reverse().flatMap(msg => [
          { role: "user", content: msg.user_message },
          { role: "assistant", content: msg.ai_response }
        ]);
        setMessages([messages[0], ...history]);
        setSessionId(res.data[0]?.session_id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });
      
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (error) {
      toast.error("Error al enviar mensaje. Intenta de nuevo.");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    { icon: BookOpen, text: "¿Cuáles son los requisitos para la visa B1/B2?" },
    { icon: HelpCircle, text: "¿Qué documentos necesito para la entrevista?" },
    { icon: Shield, text: "¿Cómo me preparo para la entrevista consular?" },
    { icon: Lightbulb, text: "¿Qué preguntas hacen en la entrevista?" }
  ];

  // Function to format message content with styled tip section
  const formatMessage = (content) => {
    const tipMatch = content.match(/💡\s*CONSEJO\s*DIVÍDELO:?\s*([\s\S]*?)$/i);
    
    if (tipMatch) {
      const mainContent = content.replace(/💡\s*CONSEJO\s*DIVÍDELO:?\s*[\s\S]*$/i, '').trim();
      const tipContent = tipMatch[1].trim();
      
      return (
        <>
          <p className="whitespace-pre-wrap">{mainContent}</p>
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs font-semibold text-emerald-700 uppercase">Consejo Divídelo</span>
                <p className="text-sm text-emerald-800 mt-1">{tipContent}</p>
              </div>
            </div>
          </div>
        </>
      );
    }
    
    return <p className="whitespace-pre-wrap">{content}</p>;
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="help-center-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-navy-900">
                Asistente Divídelo
              </h1>
              <p className="text-navy-500">
                Tu asesor migratorio virtual disponible 24/7
              </p>
            </div>
          </div>
        </motion.div>

        {/* Features Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
        >
          {[
            { icon: BookOpen, label: "Requisitos", color: "blue" },
            { icon: MessageCircle, label: "Entrevista", color: "purple" },
            { icon: Shield, label: "Documentos", color: "emerald" },
            { icon: Lightbulb, label: "Consejos", color: "amber" }
          ].map((item, i) => (
            <div key={i} className={`p-3 rounded-xl bg-${item.color}-50 border border-${item.color}-100`}>
              <item.icon className={`w-5 h-5 text-${item.color}-600 mb-1`} />
              <span className={`text-sm font-medium text-${item.color}-700`}>{item.label}</span>
            </div>
          ))}
        </motion.div>

        {/* Chat Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-navy-100 shadow-sm overflow-hidden"
        >
          {/* Chat Messages */}
          <ScrollArea className="h-[450px] p-6" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-start gap-3 ${
                    msg.role === "user" ? "flex-row-reverse" : ""
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    msg.role === "user" 
                      ? "bg-gradient-to-br from-blue-500 to-blue-600" 
                      : "bg-gradient-to-br from-emerald-500 to-emerald-600"
                  }`}>
                    {msg.role === "user" ? (
                      <User className="w-5 h-5 text-white" />
                    ) : (
                      <Bot className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className={`max-w-[80%] p-4 rounded-2xl ${
                    msg.role === "user" 
                      ? "bg-blue-600 text-white rounded-br-sm" 
                      : "bg-navy-50 text-navy-800 rounded-bl-sm border border-navy-100"
                  }`}>
                    {msg.role === "assistant" ? formatMessage(msg.content) : (
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </motion.div>
              ))}
              
              {loading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-emerald-500 to-emerald-600">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div className="bg-navy-50 p-4 rounded-2xl rounded-bl-sm border border-navy-100">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                      <span className="text-sm text-navy-500">Analizando tu consulta...</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </ScrollArea>

          {/* Suggested Questions */}
          {messages.length <= 1 && (
            <div className="px-6 pb-4 border-t border-navy-100 pt-4">
              <p className="text-sm text-navy-500 mb-3 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Preguntas frecuentes:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setInput(question.text)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm bg-navy-50 hover:bg-navy-100 text-navy-700 rounded-xl transition-colors text-left"
                  >
                    <question.icon className="w-4 h-4 text-navy-400 flex-shrink-0" />
                    <span>{question.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-navy-100 bg-navy-50/50">
            <div className="flex gap-3">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu pregunta sobre el proceso de visa..."
                className="flex-1 bg-white border-navy-200"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                data-testid="send-message-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-navy-400 mt-2 text-center">
              Asistente potenciado por IA • Consulta con tu oficial migratorio para casos específicos
            </p>
          </div>
        </motion.div>

        {/* Earn Money Banner - Only for clients */}
        <div className="mt-6">
          <EarnMoneyBanner userRole={user?.role} />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default HelpCenter;
